import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { find_feature, has_feature } from '../utils/AppUtils';
import ReviewModal from '../views/ReviewModal';
import '../App.css';

const load_sessions = async (user_id, token, set_sessions, set_loading, set_error) => {
	set_loading(true);
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const response = await axios.get(Constants.SERVER_URL + '/api/call/sessions', { headers });
		const sorted = (response.data.sessions || []).sort((a, b) => {
			return new Date(b.createdAt) - new Date(a.createdAt);
		});
		set_sessions(sorted);
	} catch (error) {
		set_error('Failed to load sessions.');
	} finally {
		set_loading(false);
	}
};

const delete_session = async (session_id, token, set_sessions) => {
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		await axios.delete(Constants.SERVER_URL + '/api/call/session/' + session_id, { headers });
		set_sessions(prev => prev.filter(s => s.id !== session_id));
	} catch (error) {
		console.log('delete_session error: ', error.message);
	}
};

const render_session_grade = (session, on_click_grade) => {
	if(!session.review_grade) {
		return null;
	}

	return (
		<span
			className={'calls-session-grade calls-session-grade-' + session.review_grade.toLowerCase()}
			onClick={e => { e.stopPropagation(); on_click_grade(session.id); }}>
			{session.review_grade} {session.review_percentage}%
		</span>
	);
};

const render_session_row = (session, navigate, token, set_sessions, on_click_grade, highlighted_session_id, row_refs) => {
	const date = new Date(session.createdAt).toLocaleDateString();
	const is_highlighted = session.id === highlighted_session_id;
	const class_name = 'calls-session-row' + (is_highlighted ? ' calls-session-row-highlighted' : '');

	return (
		<div
			key={session.id}
			ref={el => { row_refs.current[session.id] = el; }}
			className={class_name}
			onClick={() => navigate('/call/' + session.id)}>
			<span className="calls-session-name">{session.name || session.template_name}</span>
			<div className="calls-session-row-right">
				<span className="calls-session-date">{date}</span>
				{render_session_grade(session, on_click_grade)}
				<Button
					size="sm"
					variant="outline-danger"
					className="calls-session-delete-btn"
					onClick={e => { e.stopPropagation(); delete_session(session.id, token, set_sessions); }}>
					✕
				</Button>
			</div>
		</div>
	);
};

function CallSessionList({ highlighted_session_id, on_grade_click }) {
	const { token, subscription_features, is_superadmin } = useAuth();
	const navigate = useNavigate();

	const user_id = localStorage.getItem(Constants.USER_ID);
	const coach_review_feature = find_feature(Constants.c_FeatureCodes.AI_COACH_REVIEW, subscription_features);
	const has_coach_review = has_feature(coach_review_feature, is_superadmin);
	const row_refs = useRef({});

	const [sessions, set_sessions] = useState([]);
	const [loading, set_loading] = useState(false);
	const [error, set_error] = useState('');
	const [show_review, set_show_review] = useState(false);
	const [review, set_review] = useState(null);
	const [review_loading, set_review_loading] = useState(false);

	useEffect(() => {
		if(user_id) {
			load_sessions(user_id, token, set_sessions, set_loading, set_error);
		}
	}, [token]);

	useEffect(() => {
		if(!highlighted_session_id) {
			return;
		}
		const el = row_refs.current[highlighted_session_id];
		if(el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		}
	}, [highlighted_session_id]);

	const open_review = async (session_id) => {
		set_review(null);
		set_show_review(true);
		set_review_loading(true);

		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const response = await axios.post(
				Constants.SERVER_URL + '/api/call/session/' + session_id + '/review',
				{},
				{ headers }
			);
			set_review(response.data.review);
		} catch (error) {
			set_error('Failed to load review.');
		} finally {
			set_review_loading(false);
		}
	};

	return (
		<div className="calls-sessions-section">
			<div className="calls-sessions-header">
				<h5 className="calls-section-title">Recent calls</h5>
			</div>
			<div className="calls-sessions-scroll">
				{error ? <Alert variant="danger">{error}</Alert> : null}
				{loading ? (
					<div className="call-loading"><Spinner animation="border" size="sm" /></div>
				) : sessions.length === 0 ? (
					<p className="call-empty">No calls yet.</p>
				) : (
					<div className="calls-sessions-list">
						{sessions.map(session => render_session_row(session, navigate, token, set_sessions, on_grade_click || open_review, highlighted_session_id, row_refs))}
					</div>
				)}
			</div>
			{!on_grade_click && (
				<ReviewModal
					show={show_review}
					on_hide={() => set_show_review(false)}
					review={review}
					loading={review_loading}
					has_coach_review={has_coach_review}
				/>
			)}
		</div>
	);
}

export default CallSessionList;
